import { onboardingGuideContent } from '@components/onboarding/onboardingContent'
import { listDismissedItems, updateDismissedItem } from '@kudos/http-client'
import { OnboardingContent } from '@nav/types'
import { useRollbar } from '@rollbar/react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'

// Global state to prevent duplicate API calls
let globalDismissedItems: string[] = []
let globalIsLoadingDismissed = false
let loadPromise: Promise<void> | null = null

// Global function to get dismissed items (shared across all hooks)
export const getGlobalDismissedItems = async (): Promise<string[]> => {
  // If already loading, wait for the existing promise
  if (loadPromise) {
    await loadPromise
    return globalDismissedItems
  }

  // If already loaded, return cached data
  if (globalDismissedItems.length > 0 || globalIsLoadingDismissed) {
    return globalDismissedItems
  }

  try {
    globalIsLoadingDismissed = true

    loadPromise = (async () => {
      const dismissed = await listDismissedItems()
      const dismissedAliases = dismissed
        .filter(item => item.dismissedAt !== null)
        .map(item => item.itemAlias)
      globalDismissedItems = dismissedAliases
    })()

    await loadPromise
    return globalDismissedItems
  } catch {
    // Gracefully degrade: falls back to an empty dismissed-items list when the API
    // is unavailable, so the onboarding guide UI can still render without crashing.
    globalDismissedItems = []
    return []
  } finally {
    globalIsLoadingDismissed = false
    loadPromise = null
  }
}

// Function to refresh global dismissed items (useful after dismissing items)
export const refreshGlobalDismissedItems = () => {
  globalDismissedItems = []
  globalIsLoadingDismissed = false
  loadPromise = null
}

export const useOnboardingGuide = () => {
  const location = useLocation()
  const rollbar = useRollbar()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [shouldShowFab, setShouldShowFab] = useState(false)
  const [dismissedItems, setDismissedItems] = useState<string[]>([])
  const [isLoadingDismissed, setIsLoadingDismissed] = useState(true)
  const justDismissedRef = useRef(false)
  const hasJustDismissedRef = useRef(false)

  // Helper function to find onboarding content for the current path
  const findOnboardingContentForPath = (currentPath: string): OnboardingContent | null => {
    return onboardingGuideContent[currentPath] || null
  }

  // Load dismissed items on mount
  useEffect(() => {
    let isMounted = true

    const loadDismissedItems = async () => {
      try {
        setIsLoadingDismissed(true)
        const dismissedAliases = await getGlobalDismissedItems()

        // Don't overwrite local state if we just dismissed or component unmounted
        if (!hasJustDismissedRef.current && isMounted) {
          setDismissedItems(dismissedAliases)
        }
      } catch (error) {
        // Handle API errors gracefully - keep dismissed items as empty array
        rollbar.error(error instanceof Error ? error : String(error))
        if (!hasJustDismissedRef.current && isMounted) {
          setDismissedItems([])
        }
      } finally {
        if (isMounted) {
          setIsLoadingDismissed(false)
        }
      }
    }

    loadDismissedItems()

    return () => {
      isMounted = false
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ONBOARDING_GUIDE_ALIAS = 'onboarding-guide'

  // Check if onboarding guide is dismissed
  const isOnboardingGuideDismissed = (): boolean => {
    return dismissedItems.includes(ONBOARDING_GUIDE_ALIAS)
  }

  // Dismiss onboarding guide
  const dismissOnboardingGuide = async () => {
    justDismissedRef.current = true
    hasJustDismissedRef.current = true
    await updateDismissedItem({
      itemAlias: ONBOARDING_GUIDE_ALIAS,
      dismissed: true,
    })

    // Immediately update local state to hide FAB
    setDismissedItems(prev => [...prev, ONBOARDING_GUIDE_ALIAS])
    closeDialog()
  }

  // Re-enable onboarding guide (when coming from dashboard)
  const reEnableOnboardingGuide = async () => {
    await updateDismissedItem({
      itemAlias: ONBOARDING_GUIDE_ALIAS,
      dismissed: false,
    })

    setDismissedItems([])
  }

  // Check if current page should show the guide
  useEffect(() => {
    const currentPath = location.pathname
    const onboardingContent = findOnboardingContentForPath(currentPath)
    const hasGuideContent = !!onboardingContent
    const isDismissed = isOnboardingGuideDismissed()
    const isFromOnboardingGuide = location.state?.fromOnboardingGuide

    // Re-enable onboarding guide if navigating from onboarding guide checklist
    // But don't re-enable if we just dismissed
    if (hasGuideContent && isFromOnboardingGuide && isDismissed && !hasJustDismissedRef.current) {
      reEnableOnboardingGuide()
    }

    // Only show FAB if there's guide content and onboarding guide is not dismissed
    setShouldShowFab(hasGuideContent && !isDismissed && !isLoadingDismissed)
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, dismissedItems, isLoadingDismissed])

  // Handle auto-opening separately to avoid conflicts with dismiss logic
  useEffect(() => {
    const currentPath = location.pathname
    const onboardingContent = findOnboardingContentForPath(currentPath)
    const hasGuideContent = !!onboardingContent
    const isDismissed = isOnboardingGuideDismissed()
    const isFromOnboardingGuide = location.state?.fromOnboardingGuide

    // Auto-open dialog when navigating from dashboard (but not when dismissed)
    if (hasGuideContent && !isDismissed && isFromOnboardingGuide && !justDismissedRef.current) {
      setIsDialogOpen(true)
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state?.fromOnboardingGuide, dismissedItems])

  // Reset the just dismissed flag when pathname changes (not just state changes)
  useEffect(() => {
    justDismissedRef.current = false
    hasJustDismissedRef.current = false
  }, [location.pathname])

  // Reset refs when navigating from onboarding guide (to allow auto-opening)
  useEffect(() => {
    if (location.state?.fromOnboardingGuide) {
      justDismissedRef.current = false
      hasJustDismissedRef.current = false
    }
  }, [location.state?.fromOnboardingGuide])

  const openDialog = () => setIsDialogOpen(true)
  const closeDialog = () => setIsDialogOpen(false)

  // Get content for current page
  const getCurrentGuideContent = (): OnboardingContent | null => {
    const currentPath = location.pathname
    const onboardingContent = findOnboardingContentForPath(currentPath)

    if (!onboardingContent) return null

    return {
      content: onboardingContent.content,
      helpUrl: onboardingContent.helpUrl,
      routeElementId: onboardingContent.routeElementId,
    }
  }

  return {
    isDialogOpen,
    shouldShowFab,
    openDialog,
    closeDialog,
    dismissCurrentGuide: dismissOnboardingGuide,
    reEnableGuide: reEnableOnboardingGuide,
    guideContent: getCurrentGuideContent(),
    dismissedItems,
  }
}
